/**
 * API service for onboarding profiles
 * Communicates with backend NestJS API
 */

import { getCurrentSession } from "./cognito";
import { OnboardingData } from "@/lib/onboarding";
import { emitApiError } from "@/contexts/ApiErrorContext";

export interface CreateOnboardedProfileDto {
	userId?: string;
	step: number;
	selectedStates: string[];
	selectedCounties: string[];
	lookingForLien: "yes" | "no" | null;
	referralSource: string;
	selectedDataPoints: string[];
	customDataPoint: string;
	referralCode?: string;
	referralLink?: string;
	completed?: boolean;
}

export interface OnboardedProfileResponse {
	id: string;
	userId?: string;
	user_id?: string; // Backend might use snake_case
	step: number;
	selectedStates: string[];
	selectedCounties: string[];
	lookingForLien: "yes" | "no" | null;
	referralSource: string;
	selectedDataPoints: string[];
	customDataPoint: string;
	referralCode?: string;
	referralLink?: string;
	completed?: boolean; // Optional - might not be in response
	createdAt?: string;
	updatedAt?: string;
	user?: {
		id: string;
		firstname?: string;
		lastname?: string | null;
		stripe_id?: string | null;
		is_subscribed?: boolean;
		is_admin?: boolean;
	};
}

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

const handleApiFailure = async (
	response: Response,
	endpoint: string,
	fallbackMessage: string
) => {
	const errorData = await response.json().catch(() => ({}));
	const message =
		errorData.message || `${fallbackMessage}: ${response.statusText}`;

	emitApiError({
		status: response.status,
		message,
		endpoint,
	});

	throw new Error(message);
};
/**
 * Get the Cognito user ID (sub) from the current session
 */
export const getCognitoUserId = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const idToken = session.getIdToken();
		return idToken.payload.sub as string;
	} catch (error) {
		console.error("Failed to get Cognito user ID:", error);
		return null;
	}
};

/**
 * Get the access token for API requests
 */
const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

/**
 * Create or update onboarding profile
 */
export const createOnboardedProfile = async (
	data: OnboardingData
): Promise<OnboardedProfileResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const userId = await getCognitoUserId();
	if (!userId) {
		throw new Error("Failed to get user ID");
	}

	const profileDto: CreateOnboardedProfileDto = {
		userId,
		step: data.step,
		selectedStates: data.selectedStates,
		selectedCounties: data.selectedCounties,
		lookingForLien: data.lookingForLien,
		referralSource: data.referralSource,
		selectedDataPoints: data.selectedDataPoints,
		customDataPoint: data.customDataPoint,
		referralCode: data.referralCode,
		referralLink: data.referralLink,
		completed: true,
	};

	const endpoint = "/onboarded-profiles";
	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(profileDto),
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			endpoint,
			"Failed to create onboarding profile"
		);
	}

	return response.json();
};

/**
 * Check if current user exists in database
 * Returns true if user exists, false if user doesn't exist (401/403/404)
 */
export const checkUserExists = async (): Promise<boolean> => {
	const token = await getAccessToken();
	if (!token) {
		return false;
	}

	const endpoint = "/onboarded-profiles/me";

	try {
		const response = await fetch(`${API_BASE_URL}${endpoint}`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		// If we get 401 or 403, user doesn't exist or token is invalid
		if (response.status === 401 || response.status === 403) {
			return false;
		}

		// If we get 404, it could mean user doesn't exist OR profile doesn't exist
		// We need to check the response body or use a different endpoint
		// For now, we'll assume 404 means user doesn't exist if we can't parse the response
		if (response.status === 404) {
			// Try to parse response to see if it's a user error or profile error
			try {
				const errorData = await response.json();
				// If error message indicates user not found, return false
				if (
					errorData.message?.toLowerCase().includes("user") &&
					errorData.message?.toLowerCase().includes("not found")
				) {
					return false;
				}
			} catch {
				// Can't parse, assume user might exist but profile doesn't
				// We'll need to check this differently
			}
		}

		// If response is ok, user exists
		if (response.ok) {
			return true;
		}

		// For other errors, assume user doesn't exist
		return false;
	} catch (error) {
		console.error("Error checking if user exists:", error);
		return false;
	}
};

/**
 * Get current user's onboarding profile
 * Returns null if profile doesn't exist (but user might exist)
 * Throws error if user doesn't exist in database
 */
export const getMyOnboardedProfile =
	async (): Promise<OnboardedProfileResponse | null> => {
		const token = await getAccessToken();
		if (!token) {
			return null;
		}

		const endpoint = "/onboarded-profiles/me";

		const response = await fetch(`${API_BASE_URL}${endpoint}`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		// If 401 or 403, user doesn't exist in database
		if (response.status === 401 || response.status === 403) {
			const error = new Error("User account not found in database");
			error.name = "UserNotFoundError";
			throw error;
		}

		// If 404, profile doesn't exist (but user might exist)
		if (response.status === 404) {
			// Check if error message indicates user not found
			try {
				const responseText = await response.text();
				if (responseText) {
					const errorData = JSON.parse(responseText);
					if (
						errorData.message?.toLowerCase().includes("user") &&
						errorData.message?.toLowerCase().includes("not found")
					) {
						const error = new Error(
							"User account not found in database"
						);
						error.name = "UserNotFoundError";
						throw error;
					}
				}
			} catch {
				// If we can't parse, assume it's just profile not found
				// User exists but profile doesn't
			}
			return null; // Profile doesn't exist yet
		}

		if (!response.ok) {
			await handleApiFailure(
				response,
				endpoint,
				"Failed to get onboarding profile"
			);
		}

		// Handle empty response
		const responseText = await response.text();
		if (!responseText || responseText.trim() === "") {
			console.warn("Empty response from onboarding profile endpoint");
			return null;
		}

		try {
			return JSON.parse(responseText);
		} catch (parseError) {
			console.error(
				"Failed to parse onboarding profile response:",
				parseError
			);
			throw new Error("Invalid response from server");
		}
	};

/**
 * Get onboarding profile by user ID
 */
export const getOnboardedProfileByUserId = async (
	userId: string
): Promise<OnboardedProfileResponse | null> => {
	const token = await getAccessToken();
	if (!token) {
		return null;
	}

	const endpoint = `/onboarded-profiles/user/${userId}`;

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (response.status === 404) {
		return null;
	}

	if (!response.ok) {
		await handleApiFailure(
			response,
			endpoint,
			"Failed to get onboarding profile by user ID"
		);
	}

	return response.json();
};

/**
 * Update onboarding profile
 */
export const updateOnboardedProfile = async (
	id: string,
	updateData: Partial<CreateOnboardedProfileDto>
): Promise<OnboardedProfileResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const endpoint = `/onboarded-profiles/${id}`;

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(updateData),
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			endpoint,
			"Failed to update onboarding profile"
		);
	}

	return response.json();
};
